import { useCallback, useState, useRef, useEffect } from "react";
import { Box, Button, Card, CardContent, Grid, Table, TableBody, TableRow, Typography, CircularProgress } from "@mui/material";
import {  ValueRange } from "../../orm/dto";
import LoginRequired from "../LoginRequired";
import InfoBox from "../Common/InfoBox";
import { StyledTableCell } from "../utils";
import { FilePicker } from "../Common/FilePicker";
import { startISOXMLFinishJob, getISOXMLFinishJobStatus, downloadISOXMLFinishResult } from "../../orm/dev4ag";
import ISOXMLFinisherHelp from "../help/ISOXMLFinisherHelp";
import { DeviceSelectionField } from "./DeviceSelectionField";
import { DataRangeField } from "./DataRangeField";
import { DevicesExamples } from "./DeviceExamples";
import { BasicDateTimePicker } from "../Common/BaseDatePicker";

// const ColoredTableCell = styled(StyledTableCell)<{ cellcolor: string }>`
//     background-color: ${props => props.cellcolor};
// `;


const DataRangeHeader = ()=>{
    return (
        <TableRow>
            <StyledTableCell>Name</StyledTableCell>
            <StyledTableCell>Min</StyledTableCell>
            <StyledTableCell>Max</StyledTableCell>
            <StyledTableCell>Default</StyledTableCell>
            <StyledTableCell>Change per Second</StyledTableCell>
            <StyledTableCell>Major</StyledTableCell>
        </TableRow>
    )
}



export default function ISOXMLFinisher() {

    const [isoxmlFile, setISOXMLFile] = useState<File>()
    const [startTime, setStartTime] = useState<Date>(new Date(2023,6,30,10,11,12))
    const [tractorDescriptions, setTractorDescriptions] = useState<string>("")
    const [implementDescriptions, setImplementDescriptions] = useState<string>("")
    const [setpointRange, setSetpointRange] = useState<ValueRange>(new ValueRange(2000,4000,3000,true,500, 1000,0))
    const [workingSpeedRange, setWorkingSpeedRange] = useState<ValueRange>(new ValueRange(2000,4000,3000,true,500,1000/3.6, 0))
    const [workingWidthRange, setWorkingWidthRange] = useState<ValueRange>( new ValueRange(20000,40000,30000,true,5000, 1000,0))

    // let deviceDescriptions:string="";


    const [errorMessage, setErrorMessage] = useState<string>()
    const [isActive, setIsActive] = useState<boolean>()
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [loadingMessage, setLoadingMessage] = useState<string>('')
    const [currentJobId, setCurrentJobId] = useState<string | null>(null)
    const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null)

    // Cleanup effect to stop polling when component unmounts
    useEffect(() => {
        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
            }
        };
    }, []);

    const pollJobStatus = useCallback(async (jobId: string) => {
        try {
            const statusResult = await getISOXMLFinishJobStatus(jobId);
            
            if ('message' in statusResult) {
                setErrorMessage(statusResult.message);
                setIsLoading(false);
                setIsActive(false);
                setLoadingMessage('');
                if (pollingIntervalRef.current) {
                    clearInterval(pollingIntervalRef.current);
                    pollingIntervalRef.current = null;
                }
                return;
            }

            const { status, errorMessage: jobErrorMessage } = statusResult;
            
            if (status === 'Completed') {
                setLoadingMessage('Downloading result...');
                try {
                    await downloadISOXMLFinishResult(jobId);
                    setLoadingMessage('Download completed!');
                    setTimeout(() => {
                        setIsLoading(false);
                        setIsActive(false);
                        setLoadingMessage('');
                        setCurrentJobId(null);
                    }, 1000);
                } catch (downloadError) {
                    setErrorMessage('Failed to download result');
                    setIsLoading(false);
                    setIsActive(false);
                    setLoadingMessage('');
                }
                
                if (pollingIntervalRef.current) {
                    clearInterval(pollingIntervalRef.current);
                    pollingIntervalRef.current = null;
                }
            } else if (status === 'Failed') {
                setErrorMessage(jobErrorMessage || 'Job failed');
                setIsLoading(false);
                setIsActive(false);
                setLoadingMessage('');
                if (pollingIntervalRef.current) {
                    clearInterval(pollingIntervalRef.current);
                    pollingIntervalRef.current = null;
                }
            } else if (status === 'Processing') {
                setLoadingMessage('Processing ISOXML...');
            } else {
                setLoadingMessage('Job pending...');
            }
        } catch (error) {
            console.error('Error polling job status:', error);
            setErrorMessage('Error checking job status');
            setIsLoading(false);
            setIsActive(false);
            setLoadingMessage('');
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }
        }
    }, []);

    const onTractorChange = (text: string) =>{
        setTractorDescriptions(text);
        console.log("ImplementsText: "+implementDescriptions)
        console.log("TractorText: "+tractorDescriptions)
    }


    const onImplementChange = (text: string) =>{
        setImplementDescriptions(text)
        console.log("ImplementsText: "+implementDescriptions)
        console.log("TractorText: "+tractorDescriptions)
    }

    const onFinish = useCallback(async () => {
        // Prevent multiple simultaneous requests
        if (isActive || isLoading) {
            return;
        }

        setIsActive(true);
        setIsLoading(true);
        setLoadingMessage('Starting ISOXML finish job...');
        setErrorMessage('');

        if(startTime === undefined){
            setErrorMessage("No date selected");
            setIsActive(false);
            setIsLoading(false);
            setLoadingMessage('');
            return;
        }

        if( !workingSpeedRange.check(4,60)){
            setErrorMessage("SpeedRange is invalid, should be 4-60km/h")
            setIsActive(false);
            setIsLoading(false);
            setLoadingMessage('');
            return;
        }

        if( !workingWidthRange.check(4,60)){
            setErrorMessage("WorkingWidthRange is invalid, should be 4-60 m")
            setIsActive(false);
            setIsLoading(false);
            setLoadingMessage('');
            return;
        }

        const getCombinedDeviceDescription = ():string=>{
            var text = '<ISO11783_TaskData VersionMajor="4" VersionMinor="1" ManagementSoftwareManufacturer="" ManagementSoftwareVersion="" TaskControllerManufacturer="KRONE Smart Connect" TaskControllerVersion="1.104.49.0" DataTransferOrigin="2">'
            text += tractorDescriptions +"\n"
            text += implementDescriptions + "\n"
            text += '</ISO11783_TaskData>'
            console.log("Descriptions combined: " + text)
            return text;
        }

        try {
            // Start the job
            const jobResult = await startISOXMLFinishJob({
                ISOXMLFile: isoxmlFile,            
                Config: {
                    Setpoint : setpointRange,
                    StartTime: startTime.toISOString().split('.')[0]+"Z",
                    WorkingSpeed: workingSpeedRange,
                    WorkingWidth: workingWidthRange
                },
                DeviceDescriptions: getCombinedDeviceDescription()
            });

            if ('message' in jobResult) {
                setErrorMessage(jobResult.message);
                setIsActive(false);
                setIsLoading(false);
                setLoadingMessage('');
                return;
            }

            // Job started successfully, start polling
            setCurrentJobId(jobResult.jobId);
            setLoadingMessage('Job started, checking status...');
            
            // Start polling every 2 seconds
            pollingIntervalRef.current = setInterval(() => {
                pollJobStatus(jobResult.jobId);
            }, 2000);

            // Initial status check
            pollJobStatus(jobResult.jobId);

        } catch (error) {
            console.error('Error starting ISOXML finish job:', error);
            setErrorMessage('Failed to start job');
            setIsActive(false);
            setIsLoading(false);
            setLoadingMessage('');
        }
    }, [isoxmlFile, startTime, workingSpeedRange, workingWidthRange, setpointRange, tractorDescriptions, implementDescriptions, isActive, isLoading, pollJobStatus])

    const LoadingSpinner = () => {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2 }}>
                <CircularProgress size={40} sx={{ color: 'var(--bitrate-green)', mb: 2 }} />
                <Typography variant="body2" sx={{ mb: 2, textAlign: 'center' }}>
                    {loadingMessage}
                </Typography>
                {currentJobId && (
                    <Typography variant="caption" sx={{ color: 'gray' }}>
                        Job ID: {currentJobId}
                    </Typography>
                )}
            </Box>
        );
    };

    return (
        <LoginRequired>
            <Grid container wrap="nowrap" spacing={2} height={1} sx={{ background: '#fff' }}>
                <Grid item width='530px' sm='auto' container direction="column" spacing={2}>
                    <Grid item>
                        <Card variant="outlined" style={{ background: '#fff', color: 'var(--background-blue)' }}>
                            <CardContent style={{ background: '#fff', color: 'var(--background-blue)' }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 2, // Adjust the gap between items (using the theme's spacing scale)
                                }}
                                >
                                <Box
                                    sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    gap: 1, // Adjust the gap between the label and the picker
                                    }}
                                >                                
                                    <Typography>ZipFolder with initial ISOXML TaskSet</Typography>                                                
                                    <FilePicker onChange={(file) => setISOXMLFile(file)} />
                                </Box>
                                <Box
                                        sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'right',
                                        gap: 1, // Adjust the gap between the label and the picker
                                        }}
                                >  
                                            <Typography>Select StartDate</Typography>
                                            <BasicDateTimePicker disabled={isActive}  onDateChanged={setStartTime} date={startTime}/>
                                </Box>
                                        <DeviceSelectionField Title="Tractor" disabled={isActive} ChangeCallback={onTractorChange} SelectionList={DevicesExamples.GetTractorList()}></DeviceSelectionField>
                                        <DeviceSelectionField Title="Implements" disabled={isActive} ChangeCallback={onImplementChange} SelectionList={DevicesExamples.GetImplementList()}></DeviceSelectionField>
                                        <Table size="small">
                                            <TableBody>
                                                <DataRangeHeader/>
                                                <DataRangeField Title="Working Speed"  disabled={isActive || isLoading} DataRange={ workingSpeedRange }  onChange={setWorkingSpeedRange} Unit="km/h"/>
                                                <DataRangeField Title="Working Width" disabled={isActive || isLoading} DataRange={ workingWidthRange }  onChange={setWorkingWidthRange} Unit="m"/>
                                                <DataRangeField Title="Setpoint" disabled={isActive || isLoading} DataRange={ setpointRange }  onChange={setSetpointRange}  Unit="t"/>
                                                { isLoading &&
                                                    <TableRow>
                                                        <StyledTableCell colSpan={6}>
                                                            <LoadingSpinner />
                                                        </StyledTableCell>
                                                    </TableRow>
                                                }
                                                { errorMessage!==undefined &&
                                                    <TableRow>
                                                        <StyledTableCell colSpan={6}>
                                                            <Box sx={{ p: 2, backgroundColor: '#ffebee', borderRadius: 1, border: '1px solid #f44336' }}>
                                                                <Typography variant="body2" color="error">
                                                                    <b>Error during processing: {errorMessage}</b>
                                                                </Typography>
                                                            </Box>
                                                        </StyledTableCell>
                                                    </TableRow>
                                                }
                                                <TableRow>
                                                    <StyledTableCell>
                                                        <Button   
                                                            variant="contained" 
                                                            disabled={isActive || isLoading} 
                                                            onClick={onFinish} 
                                                            style={{ background: 'var(--bitrate-green)', color: 'var(--grey-3)' }}
                                                        >
                                                            {isLoading ? 'Processing...' : 'Finish TaskSet'}
                                                        </Button>
                                                    </StyledTableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </Box>
                           </CardContent>
                        </Card>
                    </Grid>
                </Grid>
                <InfoBox>
                    <ISOXMLFinisherHelp/>
                </InfoBox>            
            </Grid>
       </LoginRequired>
    )
}